import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Scale, Send, MessageSquare } from "lucide-react";

export default function Disputes() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [list, setList] = useState(null);
    const [active, setActive] = useState(null);
    const [msg, setMsg] = useState("");

    useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);

    const load = useCallback(() => {
        api.get("/disputes").then(({ data }) => { setList(data); if (data[0] && !active) setActive(data[0]); }).catch(() => setList([]));
    }, [active]);
    useEffect(() => { if (user) load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    const send = async () => {
        if (!msg.trim() || !active) return;
        try {
            const { data } = await api.post(`/disputes/${active.id}/message`, { message: msg });
            const updated = { ...active, messages: [...active.messages, data], status: "UNDER_REVIEW" };
            setActive(updated);
            setList((l) => l.map((d) => (d.id === updated.id ? updated : d)));
            setMsg("");
        } catch (e) { toast.error("Could not send message"); }
    };

    if (!user) return null;

    return (
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
            <h1 className="font-display text-3xl font-semibold tracking-tight">Dispute resolution</h1>
            <p className="text-sm text-muted-foreground">Resolve order issues with the other party. Our Trust & Safety team reviews escalations.</p>

            {list === null ? <Skeleton className="mt-6 h-64 w-full rounded-xl" /> : list.length === 0 ? (
                <div className="mt-6 rounded-xl border border-dashed border-border p-16 text-center">
                    <Scale className="mx-auto h-8 w-8 text-muted-foreground/50" />
                    <p className="mt-3 font-display font-semibold">No disputes</p>
                    <p className="mt-1 text-sm text-muted-foreground">All clear. You can open a dispute from a won item if anything goes wrong.</p>
                    <Button className="mt-4 rounded-[10px]" onClick={() => navigate("/dashboard/buyer")}>Go to buyer dashboard</Button>
                </div>
            ) : (
                <div className="mt-6 grid gap-6 lg:grid-cols-[0.8fr_1.2fr]">
                    <div className="space-y-2">
                        {list.map((d) => (
                            <button key={d.id} onClick={() => setActive(d)} data-testid="dispute-list-item" className={`w-full rounded-xl border p-4 text-left transition-[background-color] duration-150 ${active?.id === d.id ? "border-primary bg-accent" : "border-border bg-card hover:bg-muted"}`}>
                                <div className="flex items-center justify-between">
                                    <span className="font-mono text-xs text-muted-foreground">{d.ref}</span>
                                    <span className="rounded-full border border-border px-2 py-0.5 text-[11px]">{d.status}</span>
                                </div>
                                <p className="mt-1 font-display font-semibold">{d.auction_title}</p>
                                <p className="text-xs text-muted-foreground">{d.reason}</p>
                            </button>
                        ))}
                    </div>

                    {active && (
                        <Card data-testid="dispute-timeline" className="flex flex-col p-5">
                            <div className="border-b border-border pb-3">
                                <p className="font-display text-lg font-semibold">{active.auction_title}</p>
                                <p className="font-mono text-xs text-muted-foreground">{active.ref} · {active.status}</p>
                            </div>
                            <div className="flex-1 space-y-3 py-4">
                                {active.messages.map((m) => (
                                    <div key={m.id} className={`flex ${m.author_id === user.id ? "justify-end" : "justify-start"}`}>
                                        <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${m.author_id === user.id ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                                            <p className="mb-0.5 text-[11px] opacity-70">{m.author_name}</p>
                                            {m.message}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex items-center gap-2 border-t border-border pt-3">
                                <Input data-testid="dispute-message-input" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Type a message..." onKeyDown={(e) => e.key === "Enter" && send()} />
                                <Button onClick={send} className="rounded-[10px]" data-testid="dispute-send-button"><Send className="h-4 w-4" /></Button>
                            </div>
                        </Card>
                    )}
                </div>
            )}
        </div>
    );
}
